// THE ONE DEFINITION OF "LEADS" ON THE DASHBOARD AND IN REPORTING (2026-10-27): an ENQUIRY RECEIVED, i.e. a live relay submission
// routed to the club, counted by when it arrived. It is the Lead Log's default count with the same two exclusions (a duplicate is a
// record of a re-fire, a test is not a lead), and it is a count of ENQUIRIES, never people.
//
// ⚠️⚠️ WHY NOT COUNT `leads` DOCS: a `leads` doc is ONE PER PERSON PER CLUB and only exists for enquiries that went through the
// intake outbox (from mid-October), so Reporting under-counted a real club (Mona Vale: 16 against the Lead Log's 28). The Leads page
// and its stage tabs stay one row per person; the Dashboard and Reporting say "enquiries" where they mean this.
// PURE: no I/O, no clock.

export const NOT_ENQUIRY_STATUSES: readonly string[] = ['duplicate', 'test'];

export const ENQUIRIES_LABEL = 'Enquiries';
export const ENQUIRY_NOTE = 'Every enquiry received counts once, including a repeat enquiry from the same person. The Leads page lists people, one row each.';

/** The submission fields the rule reads (a wider object is fine). */
export interface EnquirySubmissionFacts {
  kind?: unknown;
  status?: unknown;
  routeId?: unknown;
  receivedAt?: unknown;
}

/** One enquiry, reduced to what a count needs. ⚠️ No name, phone or id. */
export interface EnquiryRow {
  accountId: string;
  receivedAt: string;
  /** The same code a lead's `source` carries (every relay enquiry is a Meta lead form). */
  source: string;
}

export const ENQUIRY_SOURCE = 'meta_lead_form';

/**
 * The club an enquiry counts for, or null when it is not an enquiry: not live, no route (unassigned), a route that points nowhere,
 * a duplicate, or a test (unless the club shows its test leads: the portal's demo clubs, decided server-side).
 */
export function enquiryAccountOf(sub: EnquirySubmissionFacts, accountOfRoute: ReadonlyMap<string, string>, testVisible?: ReadonlySet<string>): string | null {
  if (typeof sub.routeId !== 'string') return null;
  const accountId = accountOfRoute.get(sub.routeId);
  if (!accountId) return null;
  if (typeof sub.receivedAt !== 'string') return null;
  const isTest = sub.kind === 'test' || sub.status === 'test';
  if (isTest) return testVisible?.has(accountId) ? accountId : null;
  if (sub.kind !== 'live') return null;
  if (typeof sub.status === 'string' && NOT_ENQUIRY_STATUSES.includes(sub.status)) return null;
  return accountId;
}

export function enquiryRowsFrom(subs: readonly EnquirySubmissionFacts[], accountOfRoute: ReadonlyMap<string, string>, testVisible?: ReadonlySet<string>): EnquiryRow[] {
  const out: EnquiryRow[] = [];
  for (const s of subs) {
    const accountId = enquiryAccountOf(s, accountOfRoute, testVisible);
    if (accountId) out.push({ accountId, receivedAt: s.receivedAt as string, source: ENQUIRY_SOURCE });
  }
  return out;
}
